import React from 'react';

/**
 * StatCard — compact KPI tile for dashboards and summary strips.
 *
 * Anatomy:
 *   ┌────────────────────────┐
 *   │ [Icon]   LABEL         │
 *   │                        │
 *   │ 42                     │   ← primary metric
 *   │ ▲ 12% vs last week     │   ← optional delta
 *   └────────────────────────┘
 *
 * Tone drives the icon chip color:
 *   neutral | brand | success | warning | danger | info
 */
const TONE_CLASSES = {
  neutral: { chip: 'bg-slate-100 text-slate-600', value: 'var(--kr-navy-900)' },
  brand:   { chip: 'bg-[color:var(--kr-navy-50)] text-[color:var(--kr-navy-700)]', value: 'var(--kr-navy-900)' },
  success: { chip: 'bg-emerald-50 text-emerald-700', value: '#15794a' },
  warning: { chip: 'bg-[color:var(--kr-gold-50)] text-[color:var(--kr-gold-600)]', value: 'var(--kr-gold-600)' },
  danger:  { chip: 'bg-rose-50 text-rose-700', value: '#b42323' },
  info:    { chip: 'bg-sky-50 text-sky-700', value: '#1a4e88' },
};

export const StatCard = ({
  label,
  value,
  delta,
  deltaTone = 'neutral',
  icon: Icon,
  tone = 'neutral',
  hint,
  onClick,
  className = '',
  testId,
}) => {
  const t = TONE_CLASSES[tone] || TONE_CLASSES.neutral;
  const clickable = typeof onClick === 'function';
  const Wrapper = clickable ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      type={clickable ? 'button' : undefined}
      className={`kr-card text-left w-full ${clickable ? 'cursor-pointer hover:border-[color:var(--kr-gold-500)] transition-colors' : ''} ${className}`}
      data-testid={testId}
    >
      <div className="px-5 pt-4 pb-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <span
            className="text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: 'var(--kr-subtle)' }}
          >
            {label}
          </span>
          {Icon && (
            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${t.chip}`}>
              <Icon size={16} />
            </span>
          )}
        </div>
        <div
          className="text-3xl font-semibold font-heading tabular-nums leading-none"
          style={{ color: t.value }}
        >
          {value ?? '—'}
        </div>
        {(delta || hint) && (
          <div className="mt-2 flex items-center gap-2 text-xs">
            {delta && (
              <span
                className="font-medium tabular-nums"
                style={{
                  color:
                    deltaTone === 'success' ? '#15794a'
                    : deltaTone === 'danger' ? '#b42323'
                    : 'var(--kr-muted)',
                }}
              >
                {delta}
              </span>
            )}
            {hint && <span style={{ color: 'var(--kr-muted)' }}>{hint}</span>}
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default StatCard;
